import Big from 'big.js';

import type { AdvertisementV1 } from '~/api/v1/schemas/advertisement';
import type { NetV1 } from '~/api/v1/schemas/net';
import type { AdvertisementV2 } from '~/api/v2/schemas/advertisement';
import type { NetV2 } from '~/api/v2/schemas/net';
import { createBigFromDecimalOrNumber } from '~/utils/big-conversion';

/**
 * Type-safe field accessors that preserve schema-defined field requirements
 *
 * Design decisions:
 * - Required fields: Always return Big (never null/undefined)
 * - Optional fields: Return Big with default value (typically 0) for consistent API
 * - Decimal fields: Prioritize decimal string over number for precision
 */

/**
 * Check if net object is V2 API format
 */
function isNetV2(net: NetV1 | NetV2): net is NetV2 {
  return 'decimal_min_net' in net;
}

/**
 * Check if advertisement object is V2 API format
 */
function isAdvertisementV2(ad: AdvertisementV1 | AdvertisementV2): ad is AdvertisementV2 {
  return 'decimal_net' in ad;
}

/**
 * Check if advertisement is V2 and its point-up flag is enabled
 */
export function isPointupEnabled(ad: AdvertisementV1 | AdvertisementV2): ad is AdvertisementV2 {
  return 'pointup_flg' in ad && ad.pointup_flg === true;
}

/**
 * Type-safe net field access with preserved field requirements
 * Returns Big objects for precise decimal calculations
 */
export const NetFieldAccessor = {
  minNet: (net: NetV1 | NetV2): Big => {
    if (isNetV2(net)) {
      // V2: Both decimal and number are required
      const value = createBigFromDecimalOrNumber(net.decimal_min_net, net.min_net);
      return value ?? new Big(net.min_net);
    }
    // V1: Only number is required
    return new Big(net.min_net);
  },

  maxNet: (net: NetV1 | NetV2): Big | undefined => {
    if (isNetV2(net)) {
      // V2: Both fields are optional (per OpenAPI schema)
      if (net.decimal_max_net !== undefined || net.max_net !== undefined) {
        const value = createBigFromDecimalOrNumber(net.decimal_max_net, net.max_net);
        return value ?? (net.max_net !== undefined ? new Big(net.max_net) : undefined);
      }
      return undefined;
    }
    // V1: Optional field
    return net.max_net !== undefined ? new Big(net.max_net) : undefined;
  },

  certainNet: (net: NetV1 | NetV2): Big | undefined => {
    if (isNetV2(net)) {
      // V2: Both fields are optional (per OpenAPI schema)
      if (net.decimal_certain_net !== undefined || net.certain_net !== undefined) {
        const value = createBigFromDecimalOrNumber(net.decimal_certain_net, net.certain_net);
        return value ?? (net.certain_net !== undefined ? new Big(net.certain_net) : undefined);
      }
      return undefined;
    }
    // V1: Optional field
    return net.certain_net !== undefined ? new Big(net.certain_net) : undefined;
  },

  minSpecialNet: (net: NetV1 | NetV2): Big | undefined => {
    if (isNetV2(net)) {
      // V2: Both fields are optional (per OpenAPI schema)
      if (net.decimal_min_special_net !== undefined || net.min_special_net !== undefined) {
        const value = createBigFromDecimalOrNumber(net.decimal_min_special_net, net.min_special_net);
        return value ?? (net.min_special_net !== undefined ? new Big(net.min_special_net) : undefined);
      }
      return undefined;
    }
    // V1: Optional field
    return net.min_special_net !== undefined ? new Big(net.min_special_net) : undefined;
  },

  maxSpecialNet: (net: NetV1 | NetV2): Big | undefined => {
    if (isNetV2(net)) {
      // V2: Both fields are optional (per OpenAPI schema)
      if (net.decimal_max_special_net !== undefined || net.max_special_net !== undefined) {
        const value = createBigFromDecimalOrNumber(net.decimal_max_special_net, net.max_special_net);
        return value ?? (net.max_special_net !== undefined ? new Big(net.max_special_net) : undefined);
      }
      return undefined;
    }
    // V1: Optional field
    return net.max_special_net !== undefined ? new Big(net.max_special_net) : undefined;
  },
} as const;

/**
 * Type-safe advertisement field access with preserved field requirements
 * Returns Big objects for precise decimal calculations
 */
export const AdvertisementFieldAccessor = {
  net: (ad: AdvertisementV1 | AdvertisementV2): Big => {
    if (isAdvertisementV2(ad)) {
      // V2: Both decimal and number are required
      const value = createBigFromDecimalOrNumber(ad.decimal_net, ad.net);
      return value ?? new Big(ad.net);
    }
    // V1: Only number is required
    return new Big(ad.net);
  },

  specialNet: (ad: AdvertisementV1 | AdvertisementV2): Big | undefined => {
    if (isAdvertisementV2(ad)) {
      // V2: Both fields are optional (per OpenAPI schema)
      if (ad.decimal_special_net !== undefined || ad.special_net !== undefined) {
        const value = createBigFromDecimalOrNumber(ad.decimal_special_net, ad.special_net);
        return value ?? (ad.special_net !== undefined ? new Big(ad.special_net) : undefined);
      }
      return undefined;
    }
    // V1: Optional field
    return ad.special_net !== undefined ? new Big(ad.special_net) : undefined;
  },

  regularNet: (ad: AdvertisementV1 | AdvertisementV2): Big | undefined => {
    if (isAdvertisementV2(ad)) {
      // V2: Both fields are optional (per OpenAPI schema)
      if (ad.decimal_regular_net !== undefined || ad.regular_net !== undefined) {
        const value = createBigFromDecimalOrNumber(ad.decimal_regular_net, ad.regular_net);
        return value ?? (ad.regular_net !== undefined ? new Big(ad.regular_net) : undefined);
      }
      return undefined;
    }
    // V1: Does not have regular_net field
    return undefined;
  },

  specialRegularNet: (ad: AdvertisementV1 | AdvertisementV2): Big | undefined => {
    if (isAdvertisementV2(ad)) {
      // V2: Both fields are optional (per OpenAPI schema)
      if (ad.decimal_special_regular_net !== undefined || ad.special_regular_net !== undefined) {
        const value = createBigFromDecimalOrNumber(ad.decimal_special_regular_net, ad.special_regular_net);
        return value ?? (ad.special_regular_net !== undefined ? new Big(ad.special_regular_net) : undefined);
      }
      return undefined;
    }
    // V1: Does not have special_regular_net field
    return undefined;
  },
} as const;
